import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CContainer,
  CHeader,
  CHeaderNav,
  CHeaderToggler,
  CBadge,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilArrowCircleLeft,
  cilArrowCircleRight,
  cilBell,
} from "@coreui/icons";

import { AppBreadcrumb } from "./index";
import { AppHeaderDropdown } from "./header/index";
import { useUnreadNotificationCount } from "@/hooks/useNotification";
import { notification as notificationRoutes } from "@/libs/routes";

/**
 * @name AppHeader
 * @description A header component that includes a sticky header with a sidebar toggle button, theme color mode toggle (light, dark, auto), and a user account dropdown. It also displays the breadcrumb navigation at the bottom.
 * @param {Function} setSidebarShow - A function to toggle the visibility of the sidebar.
 * @returns {JSX.Element} The rendered header with a theme toggle, sidebar toggle, and user dropdown.
 */
const AppHeader = ({ setSidebarShow, breadCrumbArray = [], sidebarShow }) => {
  const headerRef = useRef();
  const navigate = useNavigate();

  const { data: unreadCountResponse } = useUnreadNotificationCount(null, () => {});
  const unreadCount = unreadCountResponse?.data?.unread_count || 0;

  /**
   * @name useEffect (scroll event)
   * @description Adds an event listener for the scroll event to toggle the "shadow-sm" class on the header when the page is scrolled.
   * @returns {void}
   */
  useEffect(() => {
    document.addEventListener("scroll", () => {
      headerRef.current &&
        headerRef.current.classList.toggle(
          "shadow-sm",
          document.documentElement.scrollTop > 0
        );
    });
  }, []);

  return (
    <>
      <CHeader
        position="sticky"
        className="mb-2 p-0 border-bottom-0"
        ref={headerRef}
      >
        <CContainer className="border-bottom px-4" fluid>
          <CHeaderToggler
            onClick={() => setSidebarShow((prev) => !prev)}
            style={{ marginInlineStart: "-14px" }}
            className="text-primary"
          >
            <CIcon
              icon={sidebarShow ? cilArrowCircleLeft : cilArrowCircleRight}
              size="xl"
            />
          </CHeaderToggler>
          <CHeaderNav className="ms-auto">
            <li className="nav-item py-1 position-relative">
              <CIcon
                icon={cilBell}
                size="lg"
                className="cursor-pointer"
                onClick={() => navigate(notificationRoutes.list)}
                title="Notifications"
              />
              {unreadCount > 0 && (
                <CBadge
                  color="danger"
                  shape="rounded-pill"
                  position="top-end"
                  className="position-absolute"
                  style={{ fontSize: "0.6rem", transform: "translate(30%, -30%)" }}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </CBadge>
              )}
            </li>
          </CHeaderNav>
          <CHeaderNav>
            <li className="nav-item py-1">
              <div className="vr h-100 mx-2 text-body text-opacity-75"></div>
            </li>
            <AppHeaderDropdown />
          </CHeaderNav>
        </CContainer>
      </CHeader>
      <CContainer className="px-3" fluid>
        <AppBreadcrumb breadcrumbs={breadCrumbArray} />
      </CContainer>
    </>
  );
};

export default AppHeader;
